import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import SkeletonLoadWrapper from 'pricing-pages-ui/components/common/loading/SkeletonLoadWrapper';
import SignupButton from 'pricing-pages-ui/components/common/SignupButton';
import { HighlightedProductsHeaderConfig } from 'pricing-pages-ui/constants/merchCards/HighlightedProductsHeaderConfig';
import { hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-ui/selectors/LoadingStateSelectors';
import { useSelector } from 'react-redux';
import { FREE } from 'self-service-api/constants/ProductLevels';
import styled from 'styled-components';
import UIFlex from 'UIComponents/layout/UIFlex';
import { ownsCrmSuiteProductLevel } from '../../selectors/ProductInventorySelectors';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import CurrentPlanTag from '../common/CurrentPlanTag';
import { SectionTitle } from '../common/SharedComponents';
import SuiteBusinessObjectives from './business-objectives/SuiteBusinessObjectives';
import { borderStyle, FillerRow, Section } from './MerchCardSharedComponents';
import HighlightedFreePriceDisplay from './price-display/HighlightedFreePriceDisplay';
const StyledInlineTitle = styled(FormattedMessage).withConfig({
  displayName: "HighlightedFreeCard__StyledInlineTitle",
  componentId: "wsmcpu-0"
})(["font-weight:600;font-size:14px;"]);
const DescriptionContainer = styled.div.withConfig({
  displayName: "HighlightedFreeCard__DescriptionContainer",
  componentId: "wsmcpu-1"
})(["line-height:2;font-size:12px;margin:0 auto;margin-top:8px;"]);

const HighlightedFreeCard = ({
  productLine,
  commonStyles,
  perMonth
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isCurrentPlanLevel = useSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel: FREE
  })); // In order for the grid to work, we need this to remain enclosed in a Fragment and not a div.
  // Children of the display grid item must get the grid-start attributes directly

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Section, {
      className: "p-top-6",
      style: Object.assign({
        borderTop: borderStyle,
        position: 'relative',
        gridRowStart: 1
      }, commonStyles),
      children: [isCurrentPlanLevel && /*#__PURE__*/_jsx(CurrentPlanTag, {}), /*#__PURE__*/_jsx(SectionTitle, {
        className: "m-all-0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "merchCards.freeCard.title"
        })
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({
        gridRowStart: 2
      }, commonStyles),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: productLine === 'crm' ? 'merchCards.freeCard.description.crm' : HighlightedProductsHeaderConfig[productLine][FREE].subtitle
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({
        gridRowStart: 3
      }, commonStyles),
      children: /*#__PURE__*/_jsx(HighlightedFreePriceDisplay, {
        perMonth: perMonth
      })
    }), /*#__PURE__*/_jsx(FillerRow, {
      row: 4,
      commonStyles: commonStyles
    }), isSeatsRedesignUngated ? /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({
        gridRowStart: 5
      }, commonStyles),
      children: /*#__PURE__*/_jsxs(DescriptionContainer, {
        children: [/*#__PURE__*/_jsx(StyledInlineTitle, {
          message: 'merchCards.freeCard.subtext'
        }), /*#__PURE__*/_jsx(UIFlex, {
          className: "text-start",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'merchCards.freeCard.noCreditCard'
          })
        })]
      })
    }) : /*#__PURE__*/_jsx(FillerRow, {
      row: 5,
      commonStyles: commonStyles
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({
        gridRowStart: 6
      }, commonStyles),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        children: /*#__PURE__*/_jsx(SignupButton, {
          sourceId: "pricing-hero",
          productLine: productLine,
          use: "primary",
          style: {
            width: '100%',
            lineHeight: '23px'
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex flex-column align-center",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 7
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-8",
      style: Object.assign({
        gridRowStart: 8
      }, commonStyles),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "merchCards.businessObjectives.includes"
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !hasPricingAndPurchasingSucceeded,
      heights: [47],
      children: /*#__PURE__*/_jsx(SuiteBusinessObjectives, {
        productLevel: FREE,
        commonStyles: commonStyles
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 p-top-3",
      style: Object.assign({
        gridRowStart: 10
      }, commonStyles, {
        borderBottom: borderStyle
      })
    })]
  });
};

export default HighlightedFreeCard;